import React, { useEffect, useState } from 'react';

import { createStyles, Paper } from '@mantine/core';
import PieChart, { ChartData } from 'components/charts/PieChart';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import usePackageCategoryRevenue from 'hooks/reports/revenue/usePackageCategoryRevenue';
import { CellProps } from 'react-table';
import { PackageCategoryRevenueReport } from 'types';

import { SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  chart: {
    paddingTop: theme.spacing.md,
    paddingLeft: 0,
    paddingRight: theme.spacing.xl,
    [theme.fn.smallerThan('md')]: {
      paddingLeft: theme.spacing.xl,
    },
  },
}));

const PackageCategoryRevenueTable = ({
  searchFilters,
  setOrderingValue,
}: {
  searchFilters: SearchFilters;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const { classes } = useStyles();
  const { formatCurrency } = useLocalization();

  const [ordering, setOrdering] = useState('revenue');
  const [ascending, setAscending] = useState(false);
  const [tableData, setTableData] = useState<PackageCategoryRevenueReport[]>([]);
  const [chartData, setChartData] = useState<ChartData[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    usePackageCategoryRevenue({
      dateStart: searchFilters.dateRange[0] ? searchFilters.dateRange[0].toISOString() : undefined,
      dateEnd: searchFilters.dateRange[1] ? searchFilters.dateRange[1].toISOString() : undefined,
      ordering: ascending ? ordering : `-${ordering}`,
      limit: '100',
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  useEffect(() => {
    if (!isLoading && data) {
      const flatData = data.pages
        .map(page => page.data.results)
        .flat()
        .map(p => ({ label: p.category, value: p.revenue / 100 }));

      if (flatData.length > 5) {
        const top5 = flatData.slice(0, 5);
        const allOtherSum = flatData.slice(5).reduce((prev, curr) => prev + curr.value, 0);
        setChartData([...top5, { label: 'All Others', value: allOtherSum }]);
      } else {
        setChartData(flatData);
      }
      setChartData(
        data.pages
          .map(page => page.data.results)
          .flat()
          .map(p => ({ label: p.category, value: p.revenue / 100 })),
      );
    }
  }, [isLoading, data, setChartData]);

  const columns = [
    {
      Header: 'Category',
      accessor: 'category' as const,
    },
    {
      Header: 'Revenue',
      accessor: 'revenue' as const,
      Cell: ({ value }: CellProps<PackageCategoryRevenueReport>) => formatCurrency(value / 100),
    },
  ];

  return (
    <div className={classes.content}>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['category', 'revenue']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
      {isSuccess && chartData.length > 0 && (
        <div className={classes.chart}>
          <Paper withBorder shadow='md' radius='md'>
            <PieChart data={chartData} />
          </Paper>
        </div>
      )}
    </div>
  );
};

export default PackageCategoryRevenueTable;
